import { ConsolidationStatusEnum, WeightModeEnum } from '@api/ringil-api.v2.tsx';
import type {
  SnRouteConsolidationGroupOutDTO,
  SnRouteConsolidationOutDTO,
  SnRouteConsolidationRecordOutDTO,
  SupplierNotificationToExpeditionOutDTO,
} from '@api/ringil-api.v2.tsx';
import { MAX_MERGED_EXPEDITIONS } from '@app/pages/expedition/create/mergeEligibility.ts';

/**
 * How the outcome of a route optimization is laid out for the user.
 *
 * Every selected notification ends up somewhere on screen: in a group that can become a shipment, in
 * a group of one (nothing to merge it with), or among the ones that could not be optimized at all —
 * with the reason, since "it was left out" without a why is the first thing the user would ask about.
 */

type Translate = (key: string, fallback: string, data?: object) => string;

/** The backend takes 1–100 ids; optimizing a single notification has nothing to merge it with. */
export const MIN_OPTIMIZED_NOTIFICATIONS = 2;
export const MAX_OPTIMIZED_NOTIFICATIONS = 100;

/** Why Optimize is unavailable for this many selected notifications, or nothing when it is available. */
export const optimizeDisabledReason = (selectedCount: number, tr: Translate): string | undefined => {
  if (selectedCount < MIN_OPTIMIZED_NOTIFICATIONS)
    return tr('RouteConsolidation.optimizeNeedsTwo', 'Select at least {{min}} supplier notifications to optimize.', { min: MIN_OPTIMIZED_NOTIFICATIONS });
  if (selectedCount > MAX_OPTIMIZED_NOTIFICATIONS)
    return tr('RouteConsolidation.optimizeTooMany', 'At most {{max}} supplier notifications can be optimized at once; {{count}} are selected.', {
      max: MAX_OPTIMIZED_NOTIFICATIONS,
      count: selectedCount,
    });
  return undefined;
};

/** A group the optimizer could not pair with anything: one notification, its own truck. */
export const isSoloGroup = (group: SnRouteConsolidationGroupOutDTO): boolean => (group.supplierNotificationIds?.length ?? 0) < 2;

export interface ConsolidationOverview {
  /** Groups that merge notifications, the biggest saving first. */
  groups: SnRouteConsolidationGroupOutDTO[];
  /** Groups of one, shown collapsed under "No merge found". */
  soloGroups: SnRouteConsolidationGroupOutDTO[];
  /** Notifications that were not optimized, with their status and reasons. */
  notOptimized: SnRouteConsolidationRecordOutDTO[];
  notificationsInGroups: number;
  totalKmSavings: number;
}

export const overviewOf = (result: SnRouteConsolidationOutDTO): ConsolidationOverview => {
  const all = result?.groups ?? [];
  const groups = all.filter(group => !isSoloGroup(group)).sort((a, b) => (b.kmSavings ?? 0) - (a.kmSavings ?? 0));

  return {
    groups,
    soloGroups: all.filter(isSoloGroup),
    notOptimized: (result?.supplierNotifications ?? []).filter(record => record.consolidationStatus !== ConsolidationStatusEnum.OK),
    notificationsInGroups: groups.reduce((count, group) => count + group.supplierNotificationIds.length, 0),
    totalKmSavings: groups.reduce((sum, group) => sum + (group.kmSavings ?? 0), 0),
  };
};

/** A shipment already created from a group, as the save answered. */
export interface CreatedFromGroup {
  expeditionId: string;
  applicationId: string;
}

/**
 * Why a group cannot become a shipment, or nothing when it can. Only one group's shipment is created
 * at a time, and creating one leaves the others as they were (Q10).
 */
export const createDisabledReason = (group: SnRouteConsolidationGroupOutDTO, created: CreatedFromGroup | undefined, tr: Translate): string | undefined => {
  if (created)
    return tr('RouteConsolidation.createAlreadyCreated', 'A shipment has already been created from this group: {{applicationId}}', {
      applicationId: created.applicationId,
    });
  if (!group.routeValid) return tr('RouteConsolidation.createRouteInvalid', 'The optimized route could not be turned into a valid shipment route.');
  // Only reachable by removing locations: the optimizer never returns a group of one as mergeable.
  if (group.supplierNotificationIds.length < 2)
    return tr('RouteConsolidation.createNeedsTwo', 'At least two supplier notifications have to remain to create a merged shipment.');
  if (group.exceedsMergeLimit)
    return tr('RouteConsolidation.createOverMergeLimit', 'At most {{max}} supplier notifications can be merged into one shipment; this group has {{count}}.', {
      max: MAX_MERGED_EXPEDITIONS,
      count: group.supplierNotificationIds.length,
    });
  return undefined;
};

/**
 * Removing locations from a group before creating its shipment.
 *
 * The edit is stored as the removed stops only (by `loadingOrder`) and everything else is derived from
 * the untouched group, so adding a stop back restores exactly what was there — including stops that
 * were dropped only because the removal left them empty.
 */

/** The notifications handled (loaded or unloaded) at the removed stops; they leave the whole group. */
export const removedNotificationsOf = (group: SnRouteConsolidationGroupOutDTO, removedStops: number[]): Set<string> =>
  new Set((group.stops ?? []).filter(stop => removedStops.includes(stop.loadingOrder)).flatMap(stop => stop.entries.map(entry => entry.supplierNotificationId)));

/**
 * What a stop is after the edit: removed by the user (can be added back), emptied because every
 * notification it handled went with another removed stop, or still on the route.
 */
export const stopStateOf = (
  stop: SnRouteConsolidationGroupOutDTO['stops'][number],
  removedStops: number[],
  removedNotifications: Set<string>,
): 'kept' | 'removed' | 'emptied' => {
  if (removedStops.includes(stop.loadingOrder)) return 'removed';
  return stop.entries.every(entry => removedNotifications.has(entry.supplierNotificationId)) ? 'emptied' : 'kept';
};

/**
 * The group as it will be created: without the removed notifications anywhere, and without the
 * stops and route stops that are left with nothing to do. The order of what remains is untouched —
 * dropping a notification's pick-up and its drop-off together cannot put another one out of order.
 * Distances and utilisation stay the optimizer's figures for the full group.
 */
export const editedGroupOf = (group: SnRouteConsolidationGroupOutDTO, removedStops: number[]): SnRouteConsolidationGroupOutDTO => {
  if (removedStops.length === 0) return group;
  const removed = removedNotificationsOf(group, removedStops);
  const kept = (id: string) => !removed.has(id);
  const supplierNotificationIds = group.supplierNotificationIds.filter(kept);

  return {
    ...group,
    supplierNotificationIds,
    exceedsMergeLimit: supplierNotificationIds.length > MAX_MERGED_EXPEDITIONS,
    stops: group.stops.map(stop => ({ ...stop, entries: stop.entries.filter(entry => kept(entry.supplierNotificationId)) })).filter(stop => stop.entries.length > 0),
    routeStops: group.routeStops
      .map(routeStop => ({
        ...routeStop,
        loadSupplierNotificationIds: routeStop.loadSupplierNotificationIds.filter(kept),
        unloadSupplierNotificationIds: routeStop.unloadSupplierNotificationIds.filter(kept),
      }))
      .filter(routeStop => routeStop.loadSupplierNotificationIds.length + routeStop.unloadSupplierNotificationIds.length > 0),
  };
};

/**
 * Utilisation of an edited group.
 *
 * The optimizer only sends utilisation for the group it computed. After locations are removed it is
 * estimated: utilisation is the fullest the truck gets anywhere on the route, so the remaining
 * notifications' peak load is compared with the full group's peak along the same route and the
 * optimizer's figure is scaled by that ratio. An unedited group therefore keeps the optimizer's
 * numbers exactly, and its truck model is not second-guessed. Stacking is not modelled, so this stays
 * an estimate and is shown as one.
 */

/** What a notification puts on the truck: weight, and the floor its items cover (width × length × amount). */
export interface SnLoad {
  weight: number;
  floor: number;
}

const positive = (value: number | undefined) => (Number.isFinite(value) && value > 0 ? value : 0);

export const snLoadOf = (notification: SupplierNotificationToExpeditionOutDTO): SnLoad => {
  const orders = (notification.locations ?? []).flatMap(location => location.loadedOrders ?? []);
  const items = orders.flatMap(order => order.items ?? []);
  // An order's total wins; without one its items are added up, a per-piece weight times the amount.
  const itemWeight = (order: (typeof orders)[number]) =>
    (order.items ?? []).reduce((sum, item) => sum + positive(item.weight) * (item.weightMode === WeightModeEnum.SKU_WEIGHT ? positive(item.amount) : 1), 0);

  return {
    weight: orders.reduce((sum, order) => sum + (positive(order.totalWeight) || itemWeight(order)), 0),
    floor: items.reduce((sum, item) => sum + positive(item.width) * positive(item.length) * positive(item.amount), 0),
  };
};

/** The fullest the truck gets on the route: at each stop the unloading happens before the loading. */
export const peakLoadOf = (routeStops: SnRouteConsolidationGroupOutDTO['routeStops'], loads: Map<string, SnLoad>): SnLoad => {
  const onBoard = { weight: 0, floor: 0 };
  const peak = { weight: 0, floor: 0 };
  [...routeStops]
    .sort((a, b) => a.sequence - b.sequence)
    .forEach(routeStop => {
      routeStop.unloadSupplierNotificationIds.forEach(id => {
        onBoard.weight -= loads.get(id)?.weight ?? 0;
        onBoard.floor -= loads.get(id)?.floor ?? 0;
      });
      routeStop.loadSupplierNotificationIds.forEach(id => {
        onBoard.weight += loads.get(id)?.weight ?? 0;
        onBoard.floor += loads.get(id)?.floor ?? 0;
      });
      peak.weight = Math.max(peak.weight, onBoard.weight);
      peak.floor = Math.max(peak.floor, onBoard.floor);
    });
  return peak;
};

/**
 * Estimated utilisation of `edited`, or null when there is nothing to estimate from: the group is not
 * edited, some notification's load is not known (yet), or the full group carries nothing measurable.
 * Each figure is estimated on its own, so a group whose items have weights but no dimensions still
 * gets a weight estimate.
 */
export const estimatedUtilisationOf = (
  original: SnRouteConsolidationGroupOutDTO,
  edited: SnRouteConsolidationGroupOutDTO,
  loads: Map<string, SnLoad>,
): { floorUtilisation?: number; weightUtilisation?: number } | null => {
  if (edited === original || !original.supplierNotificationIds.every(id => loads.has(id))) return null;

  const full = peakLoadOf(original.routeStops, loads);
  const remaining = peakLoadOf(edited.routeStops, loads);
  const scale = (utilisation: number, part: number, whole: number) => (whole > 0 ? (utilisation ?? 0) * (part / whole) : undefined);

  return {
    floorUtilisation: scale(original.floorUtilisation, remaining.floor, full.floor),
    weightUtilisation: scale(original.weightUtilisation, remaining.weight, full.weight),
  };
};

const STATUS_LABELS: Record<ConsolidationStatusEnum, [string, string]> = {
  [ConsolidationStatusEnum.OK]: ['RouteConsolidation.status.OK', 'OK'],
  [ConsolidationStatusEnum.OUT_OF_SCOPE]: ['RouteConsolidation.status.OUT_OF_SCOPE', 'Cannot be optimized'],
  [ConsolidationStatusEnum.NO_MODEL]: ['RouteConsolidation.status.NO_MODEL', 'No model for this customer yet'],
  [ConsolidationStatusEnum.NOT_ELIGIBLE]: ['RouteConsolidation.status.NOT_ELIGIBLE', 'Cannot be merged'],
};

export const statusLabel = (status: ConsolidationStatusEnum, tr: Translate): string => {
  const [key, fallback] = STATUS_LABELS[status] ?? [`RouteConsolidation.status.${status}`, String(status)];
  return tr(key, fallback);
};

/** English for the reasons known today. The AI may add new ones; those show as their raw code. */
const REASON_FALLBACKS: Record<string, string> = {
  NOT_FOUND: 'Not found by the optimizer',
  AMBIGUOUS_ID: 'Ambiguous identifier',
  MISSING_ORDER: 'No order',
  MISSING_LOADING_DATE: 'No loading date',
  MISSING_COORDINATES_LAT: 'Loading place has no latitude',
  MISSING_COORDINATES_LONG: 'Loading place has no longitude',
  MISSING_UNLOADING_COORDINATES_LAT: 'Unloading place has no latitude',
  MISSING_UNLOADING_COORDINATES_LONG: 'Unloading place has no longitude',
  MISSING_UNIT_DIMENSIONS: 'Items without dimensions',
  MISSING_WEIGHT: 'No weight',
  MISSING_QUANTITY: 'No quantity',
  DELETED_CUSTOMER: 'Customer was deleted',
  UNSERVICEABLE: 'Cannot be served by any truck',
  NOT_RETURNED: 'Not returned by the optimizer',
  ALREADY_MERGED: 'Already merged into a shipment',
  DXX: 'DXX notifications cannot be merged',
  CANCELED: 'Canceled',
  ARCHIVED: 'Archived',
  IN_GROUP_DEMAND: 'Part of a group demand',
  NOT_READY: 'Not ready',
};

export const reasonLabel = (code: string, tr: Translate): string => tr(`RouteConsolidation.reason.${code}`, REASON_FALLBACKS[code] ?? code);

/** 0.62 → "62 %"; the backend sends utilisation as a fraction. */
export const percentOf = (fraction: number | undefined): number => Math.round(Math.min(Math.max(fraction ?? 0, 0), 1) * 100);

/** Groups are named A, B, C… in the order shown, so a route on the map can be told apart without its colour. */
export const groupLetter = (index: number): string => String.fromCharCode(65 + (index % 26)) + (index >= 26 ? String(Math.floor(index / 26)) : '');

export interface GroupRoute {
  groupId: number;
  letter: string;
  /** Undefined past the palette: such a group is not drawn rather than given a recycled colour. */
  color?: string;
  /** Pins in driving order, labelled with the letter and the stop number ("A1", "A2"…). */
  stops: { lat: number; lng: number; label: string }[];
}

/**
 * Each group as a route for the shared map: letter and colour by its place in `groups` (the shown
 * order, biggest saving first), so a group keeps both however many others are drawn. A stop without
 * usable coordinates stays in the list but has nothing to pin.
 */
export const groupRoutesOf = (groups: SnRouteConsolidationGroupOutDTO[], palette: readonly string[]): GroupRoute[] =>
  (groups ?? []).map((group, index) => {
    const letter = groupLetter(index);
    return {
      groupId: group.groupId,
      letter,
      color: palette[index],
      stops: [...(group.stops ?? [])]
        .sort((a, b) => a.loadingOrder - b.loadingOrder)
        .filter(stop => Number.isFinite(stop.lat) && Number.isFinite(stop.lng))
        .map(stop => ({ lat: stop.lat, lng: stop.lng, label: `${letter}${stop.loadingOrder}` })),
    };
  });

/**
 * The notifications a result was computed for — the backend answers with exactly one entry per
 * requested id — so Recalculate repeats the same request whatever the table selection is now.
 */
export const requestedIdsOf = (result: SnRouteConsolidationOutDTO): string[] => (result?.supplierNotifications ?? []).map(record => record.supplierNotificationId);
